import * as R from 'ramda';
import { LiquidityAmount, TokenAmount } from '@akropolis-web/primitives';

import { FallbackedTotalUSD, Price } from 'domain/types';
import { sumEitherLiquidityAmounts, sumPartialLiquidityAmounts } from 'domain/utils';
import { Either, isLeft, isRight } from 'utils/either';
import { sumAmounts } from 'utils/amount';

function getTokenAmounts<B extends Record<string, TokenAmount>>(
  totals: { price: Either<Price>; balance: B }[],
  type: keyof B,
  withPrise: boolean,
): TokenAmount[] {
  const filteredAssetTotals = totals.filter(x => (withPrise ? isRight(x.price) : isLeft(x.price)));
  const tokenAmounts = Object.values(
    R.groupBy(
      balance => balance.currency.address,
      filteredAssetTotals.map(m => m.balance[type]),
    ),
  ).map(([balance, ...otherBalances]) => sumAmounts(otherBalances, balance));

  return tokenAmounts;
}

export function calcFallbackedTotalUsd<
  B extends Record<string, TokenAmount>,
  BU extends Record<keyof B, LiquidityAmount>,
>(
  balances: { price: Either<Price>; balance: B; balanceUSD: Either<BU> }[],
  type: keyof B,
  isExcludeZeroAmount?: boolean,
): FallbackedTotalUSD {
  const amountsWithPrice = getTokenAmounts(balances, type, true);
  const amountsWithoutPrice = getTokenAmounts(balances, type, false);

  const eitherUsdBalances = balances.map(t => t.balanceUSD.map(b => b[type]));
  const usdTotals = sumEitherLiquidityAmounts(eitherUsdBalances);
  const partialUsdTotals = sumPartialLiquidityAmounts(eitherUsdBalances);

  return {
    value: usdTotals,
    fallback: {
      partialTotalUSD: partialUsdTotals,
      amountsWithPrice: isExcludeZeroAmount
        ? amountsWithPrice.filter(a => a.gt(0))
        : amountsWithPrice,
      amountsWithoutPrice: isExcludeZeroAmount
        ? amountsWithoutPrice.filter(a => a.gt(0))
        : amountsWithoutPrice,
    },
  };
}
